import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Alert,
    AlertTitle,
    useToast,
    Heading,
    Button,
    Flex,
    HStack
} from "@chakra-ui/react";
import { FaPlus, FaLandmark } from "react-icons/fa";
import CreateRecurringTicketModal from '../components/tickets/recurring/CreateRecurringTicketModal';
import ImagePreviewModal from '../components/tickets/ImagePreviewModal';
import RecurringTicketStatusTabs from '../components/tickets/recurring/RecurringTicketStatusTabs';
import useTickets from "../hooks/useTickets";
import { useNavigate } from "react-router-dom";
import { recurringTicketStatuses } from "../constants/ticketStatusConstants";
import StatusChangeModal from "../components/tickets/StatusChangeModal";
import CompletionMarkModal from "../components/tickets/recurring/CompletionMarkModal";
import RecurrenceChangeModal from "../components/tickets/recurring/RecurrenceChangeModal";


const RecurringTickets = () => {
    const navigate = useNavigate();
    const { recurringTickets, error, createRecurringTicket, fetchRecurringTickets, updateRecurringTicketStatus, updateRecurrenceStatus, confirmCompletion } = useTickets();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const toast = useToast();
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newStatus, setNewStatus] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
    const [isReccurenceModalOpen, setIsReccurenceModalOpen] = useState(false);
    const [newRecurrence, setNewRecurrence] = useState(null);

    useEffect(() => {
        fetchRecurringTickets();
    }, [])

    const handleCreateTicket = async (ticketData) => {
        await createRecurringTicket(ticketData, () => setIsAddModalOpen(false));
    };

    const closeCompletionModal = () => {
        setIsCompletionModalOpen(false);
        setSelectedTicket(null);
    };

    const handleImagePreview = useCallback((imageUrl) => {
        setSelectedImage(imageUrl);
        setIsImagePreviewOpen(true);
    }, []);

    const confirmRecurrenceChange = useCallback((ticket, newRecurrence) => {
        setSelectedTicket(ticket);
        setNewRecurrence(newRecurrence);
        setIsReccurenceModalOpen(true);
    }, []);

    const confirmCompletionMark = useCallback((ticket) => {
        setSelectedTicket(ticket);
        setIsCompletionModalOpen(true);
    }, []);

    const confirmStatusChange = useCallback((ticket, status) => {
        if (!ticket) return;
        setSelectedTicket(ticket);
        setNewStatus(status);
        setIsStatusModalOpen(true);
    }, []);

    const handleTicketCompletion = async () => {
        if (!selectedTicket) return;

        try {
            await confirmCompletion(selectedTicket.id);
            closeCompletionModal();
        } catch (error) {
            toast({
                title: "Error",
                description: error.message || "Pogreška u označavanju.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleRecurrenceChange = async () => {
        if (!selectedTicket || newRecurrence === null) return;

        try {
            await updateRecurrenceStatus(selectedTicket.id, newRecurrence);
            setSelectedTicket(null);
            setIsReccurenceModalOpen(false);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleStatusChange = async () => {
        if (!selectedTicket || newStatus === null) return;

        try {
            await updateRecurringTicketStatus(selectedTicket.id, newStatus);
            setSelectedTicket(null);
            setIsStatusModalOpen(false);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box 
            as="main" 
            pt={{ base: "24px", md: "20px" }} 
            pr={{ base: "0px", md: "30px" }} 
            pb="20px" 
            minH="100vh" 
            pl={{ base: "0px", md: "240px" }}
        >
            <Flex justify="space-between" align="center" mb={5} pl={4} pr={4}>
                <Heading color="brand.700" size="lg">Ponavljajući zadaci</Heading>
                <HStack spacing={3}>
                    <Button
                        border="1px solid"
                        borderColor="brand.600"
                        bg="white"
                        color="brand.700"
                        _hover={{ bg: "brand.50" }}
                        onClick={() => navigate("/sections")}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        <FaLandmark />
                    </Button>

                    <Button
                        bg="brand.600"
                        color="white"
                        boxShadow="soft"
                        _hover={{ bg: "brand.700" }}
                        onClick={() => setIsAddModalOpen(true)}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        <FaPlus />
                    </Button>
                </HStack>
            </Flex>

            {error && <Alert status="error"><AlertTitle>{error}</AlertTitle></Alert>}

            <RecurringTicketStatusTabs 
                recurringTickets={recurringTickets} 
                ticketStatuses={recurringTicketStatuses} 
                confirmStatusChange={confirmStatusChange}
                confirmRecurrenceChange={confirmRecurrenceChange}
                confirmCompletionMark={confirmCompletionMark}
                handleImagePreview={handleImagePreview} />

            {/* 
                {loading && <Spinner size="xl" color="brand.700" />}
            */}
            <CreateRecurringTicketModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                handleCreateTicket={handleCreateTicket}
            />
            <ImagePreviewModal isOpen={isImagePreviewOpen} onClose={() => setIsImagePreviewOpen(false)} selectedImage={selectedImage} />
            <StatusChangeModal isOpen={isStatusModalOpen} onClose={() => setIsStatusModalOpen(false)} onConfirm={handleStatusChange} selectedTicket={selectedTicket}  />
            <CompletionMarkModal isOpen={isCompletionModalOpen} onClose={closeCompletionModal} selectedTicket={selectedTicket} handleTicketCompletion={handleTicketCompletion} />
            <RecurrenceChangeModal isOpen={isReccurenceModalOpen} onClose={() => setIsReccurenceModalOpen(false)} onConfirm={handleRecurrenceChange} selectedTicket={selectedTicket}  />
        </Box>
    );
};

export default RecurringTickets;